import React, { useState, useEffect, useMemo } from 'react'
import { useData } from '../contexts/DataContext'
import { ensureSubs, formatDate } from '../utils/helpers'

const Export = () => {
  const { exercises, results, fetchMembers, fetchExercises, fetchResults } = useData()
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [exerciseId, setExerciseId] = useState('')
  const [subId, setSubId] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchMembers()
    fetchExercises()
    fetchResults()
  }, [])

  const subsFor = (id) => {
    const exercise = exercises.find(e => e._id === id)
    return ensureSubs(exercise)
  }

  const filtered = useMemo(() => {
    return results.filter(result => {
      if (exerciseId && result.exerciseId._id !== exerciseId) return false
      if (subId && result.subId !== subId) return false
      if (from && new Date(result.createdAt) < new Date(from)) return false
      if (to && new Date(result.createdAt) > new Date(to + 'T23:59:59')) return false
      return true
    }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  }, [results, exerciseId, subId, from, to])

  const handleDownloadCSV = async () => {
    try {
      setLoading(true)

      const csvRows = ['Date,Member,Exercise,Subcategory,Data']
      filtered.forEach(result => {
        const date = new Date(result.createdAt).toISOString().split('T')[0]
        const member = result.memberId?.name || 'Unknown'
        const exercise = result.exerciseId?.name || 'Unknown'
        const sub =
          result.exerciseId?.subs?.find(s => (s._id || s.id) === result.subId)?.name ||
          result.subId ||
          'Unknown'
        const data = JSON.stringify(result.payload).replace(/"/g, '""')
        csvRows.push(`${date},"${member}","${exercise}","${sub}","${data}"`)
      })

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'bfit-results.csv')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export failed:', error)
      alert('Export failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="card grid">
      <div className="big" style={{ fontWeight: 900 }}>Export Results</div>
      
      <div className="grid three">
        <div>
          <label>From</label>
          <input 
            type="date" 
            value={from} 
            onChange={e => setFrom(e.target.value)} 
          />
        </div>
        
        <div>
          <label>To</label>
          <input 
            type="date" 
            value={to} 
            onChange={e => setTo(e.target.value)} 
          />
        </div>
        
        <div>
          <label>Exercise</label>
          <select 
            value={exerciseId} 
            onChange={e => {
              setExerciseId(e.target.value)
              setSubId('')
            }}
          >
            <option value="">All</option>
            {exercises.map(exercise => (
              <option key={exercise._id} value={exercise._id}>
                {exercise.name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label>Subcategory</label>
          <select 
            value={subId} 
            onChange={e => setSubId(e.target.value)} 
            disabled={!exerciseId}
          >
            <option value="">All</option>
            {exerciseId && subsFor(exerciseId).map(sub => (
              <option key={sub._id || sub.id} value={sub._id || sub.id}>
                {sub.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="row">
        <div className="muted">{filtered.length} rows</div>
        <button 
          className="btn slim" 
          onClick={handleDownloadCSV}
          disabled={loading}
        >
          {loading ? 'Downloading...' : 'Download CSV'}
        </button>
      </div>

      {filtered.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div className="big" style={{ fontWeight: 900, marginBottom: 12 }}>
            Preview (showing first 10)
          </div>
          
          <div className="list">
            {filtered.slice(0, 10).map(result => (
              <div key={result._id} className="item">
                <div className="row">
                  <div>
                    <div style={{ fontWeight: 800 }}>
                      {result.memberId?.name} • {result.exerciseId?.name}
                    </div>
                    <div style={{ opacity: .85 }}>
                      {JSON.stringify(result.payload)}
                    </div>
                    <div className="muted">{formatDate(result.createdAt)}</div>
                  </div>
                </div>
              </div>
            ))}
            
            {filtered.length > 10 && (
              <div className="item muted">
                ... and {filtered.length - 10} more rows
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Export
